/**
 * CII Dashboard panel — dedicated Country Instability Index view.
 *
 * Shows top 20 countries by CII score with overall score bar,
 * 4 component mini-bars (unrest, conflict, security, information),
 * and trend arrows. Click a country to fly the map there.
 */

import { eventBus } from '@/core/events';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import { getCIIClassification, getCIIColor } from '@/intelligence/analysis/cii';
import { getCountryCentroid } from '@/config/country-centroids';
import { getMap } from '@/map/engine';

export function createCIIDashboardPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'cii-dashboard-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function render(): void {
    const engine = getAnalysisEngine();
    const result = engine?.getLastResult();
    const ciiScores = result?.ciiScores;

    contentArea.textContent = '';

    if (!ciiScores || ciiScores.size === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
      const isWarmingUp = engine && !engine.isCIIActive();
      msg.textContent = isWarmingUp
        ? 'CII warming up (15-min baseline collection)\u2026'
        : 'No CII scores available';
      contentArea.appendChild(msg);
      return;
    }

    const header = document.createElement('div');
    header.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;';
    header.textContent = `Country Instability Index (${ciiScores.size})`;
    contentArea.appendChild(header);

    // Sort by overall score descending, take top 20
    const sorted = Array.from(ciiScores.values())
      .sort((a, b) => b.overall - a.overall)
      .slice(0, 20);

    for (const cii of sorted) {
      const row = document.createElement('div');
      row.style.cssText = `
        padding: 8px 10px; margin-bottom: 6px; border-radius: 6px;
        background: var(--bg-secondary); border: 1px solid var(--border-primary);
        cursor: pointer;
      `;
      row.title = `Click to view ${cii.countryName}`;

      row.onclick = () => {
        // Emit country:selected
        eventBus.emit('country:selected', { name: cii.countryName, code: cii.countryCode });
        // Fly to country
        const centroid = getCountryCentroid(cii.countryCode);
        if (centroid) {
          const map = getMap();
          if (map) {
            map.flyTo({
              center: [centroid[1], centroid[0]],
              zoom: Math.max(map.getZoom(), 4),
              duration: 800,
            });
          }
        }
      };

      // Top row: name + overall score + trend
      const topRow = document.createElement('div');
      topRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 6px;';

      const name = document.createElement('span');
      name.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary); min-width: 100px;';
      name.textContent = cii.countryName;
      topRow.appendChild(name);

      // Classification badge
      const classification = getCIIClassification(cii.overall);
      const color = getCIIColor(cii.overall);

      const classBadge = document.createElement('span');
      classBadge.style.cssText = `
        font-size: 9px; font-weight: 600; text-transform: uppercase;
        padding: 1px 6px; border-radius: 3px;
        background: ${color}22; color: ${color};
      `;
      classBadge.textContent = classification;
      topRow.appendChild(classBadge);

      // Overall score bar
      const barBg = document.createElement('div');
      barBg.style.cssText = 'flex: 1; height: 6px; border-radius: 3px; background: var(--bg-primary);';
      const barFill = document.createElement('div');
      barFill.style.cssText = `height: 100%; border-radius: 3px; width: ${cii.overall}%; background: ${color}; transition: width 300ms ease;`;
      barBg.appendChild(barFill);
      topRow.appendChild(barBg);

      const scoreNum = document.createElement('span');
      scoreNum.style.cssText = `font-size: 12px; font-weight: 700; font-family: var(--font-mono); color: ${color}; min-width: 24px; text-align: right;`;
      scoreNum.textContent = String(cii.overall);
      topRow.appendChild(scoreNum);

      // Trend arrow
      const arrow = document.createElement('span');
      arrow.style.cssText = 'font-size: 12px;';
      if (cii.trend === 'rising') {
        arrow.textContent = '\u2191';
        arrow.style.color = 'var(--severity-critical)';
        arrow.title = 'Rising';
      } else if (cii.trend === 'falling') {
        arrow.textContent = '\u2193';
        arrow.style.color = 'var(--severity-low)';
        arrow.title = 'Falling';
      } else {
        arrow.textContent = '\u2192';
        arrow.style.color = 'var(--text-muted)';
        arrow.title = 'Stable';
      }
      topRow.appendChild(arrow);

      row.appendChild(topRow);

      // Component mini-bars
      const componentsRow = document.createElement('div');
      componentsRow.style.cssText = 'display: flex; gap: 6px;';

      const componentDefs: [string, number, string][] = [
        ['U', cii.components.unrest, '#f97316'],
        ['C', cii.components.conflict, '#ef4444'],
        ['S', cii.components.security, '#8b5cf6'],
        ['I', cii.components.information, '#06b6d4'],
      ];

      for (const [abbr, value, compColor] of componentDefs) {
        const comp = document.createElement('div');
        comp.style.cssText = 'flex: 1; display: flex; align-items: center; gap: 3px;';

        const compLabel = document.createElement('span');
        compLabel.style.cssText = 'font-size: 9px; color: var(--text-muted); width: 8px;';
        compLabel.textContent = abbr;
        compLabel.title = abbr === 'U' ? 'Unrest' : abbr === 'C' ? 'Conflict' : abbr === 'S' ? 'Security' : 'Information';
        comp.appendChild(compLabel);

        const miniBar = document.createElement('div');
        miniBar.style.cssText = 'flex: 1; height: 3px; border-radius: 1.5px; background: var(--bg-primary);';
        const miniFill = document.createElement('div');
        miniFill.style.cssText = `height: 100%; border-radius: 1.5px; width: ${(value / 25) * 100}%; background: ${compColor};`;
        miniBar.appendChild(miniFill);
        comp.appendChild(miniBar);

        const compVal = document.createElement('span');
        compVal.style.cssText = 'font-size: 9px; font-family: var(--font-mono); color: var(--text-muted); width: 14px; text-align: right;';
        compVal.textContent = String(value);
        comp.appendChild(compVal);

        componentsRow.appendChild(comp);
      }

      row.appendChild(componentsRow);
      contentArea.appendChild(row);
    }
  }

  // Initial render
  render();

  // Subscribe to analysis:complete
  cleanupFns.push(
    eventBus.on('analysis:complete', () => scheduleRender()),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
